import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../shared/ui/kit/primitives';

interface DangerZoneProps {
    onReset: () => void;
    onExport: () => void;
    onImport: (file: File) => Promise<void>;
}

export const DangerZone: React.FC<DangerZoneProps> = ({
    onReset,
    onExport,
    onImport,
}) => {
    const [showConfirmReset, setShowConfirmReset] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsImporting(true);
        try {
            await onImport(file);
        } finally {
            setIsImporting(false);
            e.target.value = '';
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-red-500/[0.03] border border-red-200 dark:border-red-500/20 rounded-xl overflow-hidden"
        >
            <div className="px-4 py-3 border-b border-red-200 dark:border-red-500/20 bg-red-500/[0.05]">
                <span className="text-[10px] font-semibold text-red-600 dark:text-red-400 uppercase tracking-[0.06em]">
                    Опасная зона
                </span>
            </div>
            
            <div className="p-4 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h4 className="text-[13px] font-medium text-red-600 dark:text-red-400">
                            Сбросить все настройки
                        </h4>
                        <p className="text-[11px] text-[var(--text-muted)]">
                            Все настройки будут возвращены к значениям по умолчанию
                        </p>
                    </div>
                    
                    <AnimatePresence mode="wait">
                        {showConfirmReset ? (
                            <motion.div
                                key="confirm"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="flex gap-2"
                            >
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setShowConfirmReset(false)}
                                >
                                    Отмена
                                </Button>
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => {
                                        onReset();
                                        setShowConfirmReset(false);
                                    }}
                                >
                                    Подтвердить
                                </Button>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="button"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => setShowConfirmReset(true)}
                                >
                                    Сбросить
                                </Button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="h-px bg-red-200 dark:bg-red-500/20 my-3" />

                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h4 className="text-[13px] font-medium text-[var(--text)]">
                            Экспорт / Импорт настроек
                        </h4>
                        <p className="text-[11px] text-[var(--text-muted)]">
                            Сохраните или восстановите настройки из файла
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".json"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleImportClick}
                            loading={isImporting}
                        >
                            Импорт
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onExport}
                        >
                            Экспорт
                        </Button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};